import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import LocationBand from '../home/components/LocationBand';
import AreaCard, { type AreaData } from './components/AreaCard';
import ServiceAreasFaqSection from './components/ServiceAreasFaqSection';
import { Link } from 'react-router-dom';

const areas: AreaData[] = [
  {
    id: 'mission-beach',
    name: 'Mission Beach',
    region: 'Cassowary Coast, Far North QLD',
    tagline: 'Our Home Turf. We Know It Better Than Anyone.',
    intro:
      'Mission Beach is where Darkside Digital was built — and we know the local business landscape inside out. Whether you\'re running a tourism operation, a beach café, a trade business, or a professional service, we understand the seasonal peaks, the tight-knit community, and the specific challenge of competing in a regional market where foot traffic is seasonal and online search is year-round. We help Mission Beach businesses capture the tourist wave and the local regulars in equal measure.',
    localContext:
      'The Cassowary Coast attracts tens of thousands of visitors annually, but most local businesses aren\'t capturing their share of online search. Being the first result people see from the airport to the beach is worth more than any billboard.',
    services: ['Local SEO', 'Google Business Optimisation', 'Tourism Website Design', 'AI Chat for Seasonal Businesses', 'GEO / AEO'],
    stats: [
      { num: '11+', label: 'Years Local' },
      { num: '#1', label: 'Google Rankings Achieved' },
    ],
    image:
      'https://public.readdy.ai/ai/img_res/ff8e9bfc46cfb685d26bf392d44a7c90.jpg',
  },
  {
    id: 'cassowary-coast',
    name: 'Cassowary Coast',
    region: 'Innisfail · Tully · Cardwell',
    tagline: 'Regional. Resilient. Ready to Rank.',
    intro:
      'The Cassowary Coast — Innisfail, Tully, Cardwell and surrounds — is home to a diverse mix of agricultural businesses, trades, hospitality, and professional services. These are markets where competition may be lighter, but customers are harder to reach online. We help Cassowary Coast businesses show up when locals search, build credibility before the phone rings, and capture leads that would otherwise go to Cairns-based competitors who are easier to find.',
    localContext:
      'Innisfail and Tully have strong local economies, but most businesses are invisible online. Being the top result in your town is easier than you think — and the businesses that move first own it for years.',
    services: ['Local SEO for Regional Towns', 'Trades Website Design', 'Google Business Setup', 'Competitor Analysis', 'Reputation & Review Strategy'],
    stats: [
      { num: '3x', label: 'Lead Increase' },
      { num: '60%', label: 'After-Hours Leads Captured' },
    ],
    image:
      'https://readdy.ai/api/search-image?query=Cassowary%20Coast%20Far%20North%20Queensland%20Australia%20lush%20tropical%20rainforest%20green%20hills%20sugarcane%20fields%20Innisfail%20region%20aerial%20landscape%20misty%20mountains%20beautiful%20rural%20tropical%20countryside&width=800&height=560&seq=sa-cassowary-coast-hero&orientation=landscape',
  },
  {
    id: 'cairns',
    name: 'Cairns',
    region: 'Far North Queensland',
    tagline: 'The Big Market. We Know How to Win It.',
    intro:
      'Cairns is the commercial heart of Far North Queensland — and one of Australia\'s most competitive local search markets for tourism, hospitality, and professional services. Ranking in Cairns requires more than a website; it requires a full local SEO strategy, structured content for AI search, and a site that converts massive tourist and local traffic into real customers. We\'ve helped Cairns businesses break through against established competitors with much bigger budgets — and held those positions.',
    localContext:
      'Cairns receives over 2 million visitors annually. Most local businesses capture a fraction of that traffic online. The difference between position #1 and #5 in Cairns search is worth thousands in revenue every single month.',
    services: ['Competitive Local SEO', 'Tourism & Hospitality Websites', 'GEO / AEO for AI Search', 'Conversion Rate Optimisation', 'AI Integration'],
    stats: [
      { num: '2M+', label: 'Annual Visitor Market' },
      { num: '4.2x', label: 'Avg Booking Enquiry Increase' },
    ],
    image:
      'https://readdy.ai/api/search-image?query=Cairns%20Queensland%20Australia%20tropical%20city%20esplanade%20waterfront%20palm%20trees%20beautiful%20sunset%20skyline%20tropical%20harbour%20Far%20North%20Queensland%20Great%20Barrier%20Reef%20gateway%20tourism%20destination%20aerial%20panoramic%20view&width=800&height=560&seq=sa-cairns-hero&orientation=landscape',
  },
  {
    id: 'townsville',
    name: 'Townsville',
    region: 'North Queensland',
    tagline: "North Queensland's Largest City. A Growing Opportunity.",
    intro:
      'Townsville is North Queensland\'s largest city — a growing hub for trades, defence contractors, healthcare, retail, and professional services. The business landscape is diverse and the population is expanding, with locals increasingly turning to Google and AI to find services. Our Townsville clients span everything from allied health to construction — all using the same AI-powered approach to build better rankings, stronger digital presence, and a pipeline of inbound leads that didn\'t exist before.',
    localContext:
      "Townsville's digital market is growing fast and most local categories are still wide open. Businesses that establish strong SEO and AI search visibility now will own those positions for years — before the market catches up.",
    services: ['Local SEO Townsville', 'Trades & Services Websites', 'Healthcare Practice Marketing', 'AI Automation', 'Competitor Analysis'],
    stats: [
      { num: '200K+', label: 'Population Market' },
      { num: '98%', label: 'Client Retention Rate' },
    ],
    image:
      'https://readdy.ai/api/search-image?query=Townsville%20Queensland%20Australia%20city%20skyline%20Castle%20Hill%20landmark%20Strand%20beach%20waterfront%20Magnetic%20Island%20background%20tropical%20North%20Queensland%20coastal%20city%20beautiful%20sunny%20day%20aerial%20panoramic%20view&width=800&height=560&seq=sa-townsville-hero&orientation=landscape',
  },
];

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.darksidedigital.com.au';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Darkside Digital',
  url: siteUrl,
  telephone: '+611300029054',
  email: 'hello@darksidedigital.com.au',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Mission Beach',
    addressRegion: 'QLD',
    postalCode: '4852',
    addressCountry: 'AU',
  },
  areaServed: [
    { '@type': 'City', name: 'Mission Beach' },
    { '@type': 'AdministrativeArea', name: 'Cassowary Coast' },
    { '@type': 'City', name: 'Cairns' },
    { '@type': 'City', name: 'Townsville' },
    { '@type': 'AdministrativeArea', name: 'Far North Queensland' },
    { '@type': 'Country', name: 'Australia' },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Service Areas',
      item: `${siteUrl}/service-areas`,
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Do you only work with businesses in Far North Queensland?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No — while we\'re based in Mission Beach and have deep roots across Far North Queensland, Cairns, and the Cassowary Coast, we work with businesses all across Australia entirely remotely. Sydney, Melbourne, Brisbane, Perth, Adelaide — we\'ve delivered results in every major market. Location genuinely does not affect outcomes when the strategy and execution are strong.',
      },
    },
    {
      '@type': 'Question',
      name: 'We\'re in a small regional town. Is it worth doing SEO?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Often more worth it than in the city — and faster. Regional markets typically have less competition online, which means a well-structured local SEO campaign can reach position one within weeks rather than months. The businesses that establish that dominance early tend to hold it for years because competitors rarely catch up.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you need to visit us in person to get started?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Never. Our entire process — strategy, design, SEO, GEO, and ongoing optimisation — runs fully remotely from day one. We use video calls for strategy sessions, shared document workflows for content, and async reporting so you can review results in your own time.',
      },
    },
    {
      '@type': 'Question',
      name: 'We\'re in Melbourne. Why would we use a QLD-based agency?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Because results are results regardless of postcode. Our clients in Melbourne and Sydney aren\'t paying for proximity — they\'re paying for an AI-first SEO strategy that actually moves their rankings. Our edge is technical: AI search optimisation, clean schema architecture, and GEO programmes that most metro agencies still haven\'t figured out.',
      },
    },
    {
      '@type': 'Question',
      name: 'What\'s the difference between local SEO and national SEO?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Local SEO targets searches tied to a specific location — "web designer Cairns" or "accountant Mission Beach" — and focuses on Google Business Profile, map pack rankings, and geo-targeted content. National SEO targets broader keyword categories across all of Australia without a geographic anchor. Many of our clients run both simultaneously.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can you manage Google Business Profiles for multiple locations?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Multi-location businesses are something we handle frequently — separate optimised Google Business Profiles for each location, consistent NAP data across directories, location-specific landing pages with unique content, and individual review strategies per branch.',
      },
    },
    {
      '@type': 'Question',
      name: 'We run a tourism business in a regional area. Is there a specific strategy for that?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely — tourism search is seasonal, mobile-heavy, and heavily influenced by aggregator platforms. We build tourism SEO strategies that capture direct bookings, reduce aggregator dependency, and include GEO optimisation so your business gets cited when travellers ask AI tools for recommendations.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does GEO / AEO work for regional businesses, not just big brands?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — and regional businesses actually have an early-mover advantage right now. Most small and regional operators have zero GEO presence, which means the field is open. AI engines don\'t favour size; they favour authority, clarity, and structured answers. A regional business with a well-executed GEO programme can outrank major brands for specific regional queries.',
      },
    },
  ],
};

export default function ServiceAreasPage() {
  return (
    <main className="bg-ds-bg min-h-screen font-body">
      {/* Per-page SEO — React 19 hoists these to <head> automatically */}
      <title>Web Design &amp; SEO | Mission Beach, Cairns &amp; Far North QLD</title>
      <meta name="description" content="Darkside Digital serves businesses across Mission Beach, Cassowary Coast, Cairns, Townsville and all of Australia. AI websites, local SEO & GEO from Far North Queensland." />
      <meta name="keywords" content="web design Mission Beach, local SEO Cairns, website design Townsville, Cassowary Coast digital agency, Far North Queensland SEO, AI websites QLD" />
      <link rel="canonical" href={`${siteUrl}/service-areas`} />
      <meta name="geo.region" content="AU-QLD" />
      <meta name="geo.placename" content="Mission Beach" />
      <meta name="geo.position" content="-17.862;146.103" />
      <meta name="ICBM" content="-17.862, 146.103" />
      <meta property="og:title" content="Service Areas | Darkside Digital — Mission Beach, Cairns & Far North QLD" />
      <meta property="og:description" content="AI-powered websites, local SEO & GEO for businesses across Mission Beach, Cassowary Coast, Cairns, Townsville and all of Australia." />
      <meta property="og:url" content={`${siteUrl}/service-areas`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:title" content="Service Areas | Darkside Digital — Far North QLD & Australia" />
      <meta name="twitter:description" content="AI websites, local SEO & GEO for businesses across Mission Beach, Cairns, Townsville and all of Australia." />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-36 pb-20 bg-ds-bg overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-25 pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-ds-red/6 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6">
          <span className="section-label block mb-4">Where We Work</span>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-none mb-6">
            WEBSITE DESIGN &amp; SEO
            <br />
            MISSION BEACH, CAIRNS,
            <br />
            <span className="text-ds-red neon-text-red">FAR NORTH QLD &amp; AUSTRALIA.</span>
          </h1>

          <p className="font-body text-white/60 text-lg leading-relaxed max-w-2xl mb-10">
            Based in Mission Beach, Far North Queensland — we deliver AI-powered website design, local SEO, and GEO/AEO services to businesses across the Cassowary Coast, Cairns, Townsville, and all of Australia remotely.
          </p>

          {/* Jump links */}
          <div className="flex flex-wrap gap-3">
            {areas.map((area) => (
              <a
                key={area.id}
                href={`#${area.id}`}
                className="font-body text-xs text-white/50 border border-ds-border px-4 py-2 hover:border-ds-red/50 hover:text-white transition-all duration-200"
              >
                {area.name}
              </a>
            ))}
            <a
              href="#australia-wide"
              className="font-body text-xs text-white/50 border border-ds-border px-4 py-2 hover:border-ds-red/50 hover:text-white transition-all duration-200"
            >
              All of Australia
            </a>
          </div>

          <div className="mt-10 h-px w-24 bg-ds-red" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.4)' }} />
        </div>
      </section>

      {/* ── Area Cards ── */}
      <section className="bg-ds-bg border-t border-ds-border">
        {areas.map((area, i) => (
          <AreaCard key={area.id} area={area} index={i} />
        ))}
      </section>

      {/* ── Australia-Wide Band ── */}
      <section id="australia-wide" className="relative py-20 bg-ds-bg-2 border-t border-ds-border overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/40 to-transparent" />
        <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-96 bg-ds-red/5 blur-[100px] rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="section-label block mb-4">Remote & National</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none mb-6">
                ANYWHERE IN
                <br />
                <span className="text-ds-red neon-text-red">AUSTRALIA.</span>
              </h2>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-5">
                While our roots are in Far North Queensland, our work isn't limited by geography. We serve clients remotely across every Australian state and territory — Sydney, Melbourne, Brisbane, Perth, Adelaide, and everywhere in between.
              </p>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-8">
                Everything we do — strategy, design, SEO, GEO, AI integration — works just as effectively for a business in Perth as it does for one down the road from us. Distance has never cost a client a single result.
              </p>

              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {[
                  'National SEO Campaigns',
                  'Remote AI Implementation',
                  'Multi-Location Website Design',
                  'GEO / AEO Nationwide',
                  'Competitor Analysis Any Market',
                  'Monthly Reporting & Strategy',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-ds-red text-xs" />
                    </div>
                    <span className="font-body text-white/65 text-xs">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/#contact" className="btn-red text-xs whitespace-nowrap inline-flex">
                <span>Start a Conversation</span>
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {/* Stats panel */}
            <div className="grid grid-cols-2 gap-px bg-ds-border">
              {[
                { num: 'AU-Wide', label: 'Service Coverage' },
                { num: '126+', label: 'Businesses Helped' },
                { num: '11+', label: 'Years in Operation' },
                { num: '98%', label: 'Retention Rate' },
              ].map((stat) => (
                <div key={stat.label} className="bg-ds-bg p-8 text-center">
                  <div className="font-display text-3xl text-ds-red leading-none mb-2" style={{ letterSpacing: '0.03em' }}>
                    {stat.num}
                  </div>
                  <div className="font-body text-white/45 text-xs tracking-[0.15em] uppercase">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="relative py-20 bg-ds-bg border-t border-ds-border overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-15 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[250px] bg-ds-red/5 blur-[100px] rounded-full pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-none mb-6">
            NOT SURE IF WE
            <br />
            <span className="text-ds-red neon-text-red">COVER YOUR AREA?</span>
          </h2>
          <p className="font-body text-white/55 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            Call us or send a message. If we can help, we'll tell you exactly how. If we're not the right fit, we'll tell you that too — straight up, no runaround.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/#contact" className="btn-red text-sm whitespace-nowrap">
              <span>Get Your Free Audit</span>
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="tel:1300029054"
              className="btn-outline text-sm whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line" />
              1300 029 054
            </a>
          </div>
        </div>
      </section>

      <ServiceAreasFaqSection />
      <LocationBand />
      <Footer />
    </main>
  );
}
